<template>
  <div class="content content_in sat-page platform-card">
    <div v-if="loading" class="platform-card__loader">
      <img :src="require('@/assets/images/rolling.svg')" alt="" class="loader" />
    </div>

    <div v-else-if="!subaccount" class="sat-empty">
      <div class="sat-empty__title">{{ $t("platform.card.not_found") }}</div>
      <router-link to="/platforms" class="btn _bg-green">{{ $t("platform.card.back") }}</router-link>
    </div>

    <div v-else :key="platformId">
      <CardHeader :subaccount="subaccount" :kind="kind" @code="openTab(codeTab)" />
      <CardTabs :tabs="tabs" :current="tab" :marks="tabMarks" @select="openTab" />

      <div v-if="visited.general" v-show="tab === 'general'">
        <GeneralTab :form="form" :v="$v.form" :kind="kind" :subaccount="subaccount" :options="options" />
      </div>

      <div v-if="visited.traffic" v-show="tab === 'traffic'">
        <SettingsSection :title="$t('platform.card.sections.traffic_title')" :description="$t('platform.card.sections.traffic')">
          <TrafficFields :form="form" :v="$v.form" />
        </SettingsSection>
      </div>

      <div v-if="visited.landings" v-show="tab === 'landings'">
        <SettingsSection :title="$t('platform.card.sections.landings_title')" :description="$t('platform.card.sections.landings')">
          <LandingsPicker v-model="form.lands" :landings="options.landings" :invalid="$v.form.lands.$error" expanded />
          <small v-if="$v.form.lands.$error" class="error d-block mt-2">{{ $t("platform.card.errors.lands") }}</small>
        </SettingsSection>
      </div>

      <div v-if="visited.mailing" v-show="tab === 'mailing'">
        <SettingsSection :title="$t('platform.card.sections.mailing_title')" :description="$t('platform.card.sections.mailing')">
          <MailingFields :form="form" :options="options" />
        </SettingsSection>
      </div>

      <div v-if="visited.formats" v-show="tab === 'formats'">
        <FormatsTab :platformId="platformId" :sub="sub" :marks="formatMarks" @select="openTab('formats', $event)" />
      </div>

      <div v-if="visited.postback" v-show="tab === 'postback'">
        <SettingsSection :title="$t('platform.card.sections.postback_title')" :description="$t('platform.card.sections.postback')">
          <PostbackFields :form="form" :formatList="postbackFormats" :templates="options.templates" :invalid="$v.form.postback_url.$invalid" />
        </SettingsSection>
        <SettingsSection :title="$t('platform.card.sections.postback_logs_title')" :description="$t('platform.card.sections.postback_logs')">
          <PostbackLogs :platformId="platformId" />
        </SettingsSection>
      </div>

      <div v-if="visited[codeTab]" v-show="tab === codeTab">
        <CodeTab :platform="codePlatform" :isSite="isSite" :dirty="dirty" />
      </div>

      <SaveBar :dirty="dirty" :saving="saving" :message="saveMessage" @save="save" @discard="discard" />
    </div>
  </div>
</template>
<script>
import { validationMixin } from "vuelidate";
import { required, minLength, maxLength, url, requiredIf } from "vuelidate/lib/validators";
import ServiceSubAccs from "@/api/subaccsService";
import { showSettingsSavedAlert } from "@/helpers/selfHostedScript";
import SaveBar from "@/components/Satellites/SaveBar.vue";
import { confirmLeave } from "@/components/Satellites/unsaved";
import "@/components/Satellites/satellites.scss";
import CardHeader from "@/components/PlatformCard/CardHeader.vue";
import CardTabs from "@/components/PlatformCard/CardTabs.vue";
import SettingsSection from "@/components/PlatformCard/SettingsSection.vue";
import GeneralTab from "@/components/PlatformCard/GeneralTab.vue";
import FormatsTab from "@/components/PlatformCard/FormatsTab.vue";
import CodeTab from "@/components/PlatformCard/CodeTab.vue";
import PostbackLogs from "@/components/PlatformCard/PostbackLogs.vue";
import MailingFields from "@/components/PlatformCard/fields/MailingFields.vue";
import PostbackFields from "@/components/PlatformCard/fields/PostbackFields.vue";
import TrafficFields from "@/components/PlatformCard/fields/TrafficFields.vue";
import LandingsPicker from "@/components/PlatformCard/fields/LandingsPicker.vue";
import { loadPlatformOptions } from "@/components/PlatformCard/platformOptions";
import { allCategoriesDisabled, formFromSubaccount, formSnapshot, isSiteKind, KIND_ARBITRAGE, platformKind, postbackFormatList, subaccountPayload } from "@/components/PlatformCard/subaccountPayload";

const TABS = {
  general: { key: "general", icon: "sliders" },
  traffic: { key: "traffic", icon: "shuffle" },
  landings: { key: "landings", icon: "file" },
  mailing: { key: "mailing", icon: "bell" },
  formats: { key: "formats", icon: "layers" },
  postback: { key: "postback", icon: "send" },
  code: { key: "code", icon: "code" },
  link: { key: "link", icon: "link" },
};

const TABS_BY_KIND = {
  site: ["general", "mailing", "formats", "postback", "code"],
  landing: ["general", "mailing", "formats", "postback", "code"],
  arbitrage: ["general", "traffic", "landings", "mailing", "postback", "link"],
  directlink: ["general", "postback", "link"],
};

// Where each validated field of the form is
const FIELD_TABS = {
  name: "general",
  postback_url: "postback",
  tb_subscription_url: "traffic",
  tb_complexity_url: "traffic",
  lands: "landings",
};

// Everything about one platform on one page: /platforms/:id/:tab/:sub. The fields saved with PUT /subaccount/:id
// live here; each ad format block saves its own settings and registers itself (formatSection.js). One save bar
// saves whatever changed.
export default {
  name: "PlatformCard",
  components: { CardHeader, CardTabs, SettingsSection, GeneralTab, FormatsTab, CodeTab, PostbackLogs, MailingFields, PostbackFields, TrafficFields, LandingsPicker, SaveBar },
  mixins: [validationMixin],
  provide() {
    return {
      settingsFieldRow: true,
      cardSections: {
        register: (vm) => this.$set(this.sections, vm.sectionKey, vm),
        unregister: (vm) => {
          if (this.sections[vm.sectionKey] === vm) this.$delete(this.sections, vm.sectionKey);
        },
      },
    };
  },
  data() {
    return {
      loading: true,
      saving: false,
      subaccount: null,
      codePlatform: null,
      options: null,
      form: null,
      savedSnapshot: "",
      // Format blocks by their key
      sections: {},
      // Tabs opened once: they stay rendered, hidden, so their changes and requests are kept
      visited: {},
      // Tabs and formats the last save failed on
      failed: {},
    };
  },
  validations() {
    const form = {
      name: { required, minLength: minLength(3), maxLength: maxLength(52) },
      postback_url: { url, maxLength: maxLength(4096) },
    };
    if (this.kind === KIND_ARBITRAGE) {
      form.tb_subscription_url = {
        required: requiredIf(() => Boolean(this.form && this.form.tb_subscription === "user")),
        url,
        maxLength: maxLength(1024),
      };
      form.tb_complexity_url = {
        required: requiredIf(() => Boolean(this.form && this.form.tb_complexity === "user")),
        url,
        maxLength: maxLength(1024),
      };
      form.lands = { minLength: (value) => value.length >= 1 };
    }
    return { form };
  },
  computed: {
    platformId() {
      return Number(this.$route.params.id);
    },
    kind() {
      return this.subaccount ? platformKind(this.subaccount) : null;
    },
    isSite() {
      return isSiteKind(this.kind);
    },
    codeTab() {
      return this.isSite ? "code" : "link";
    },
    tabs() {
      return (TABS_BY_KIND[this.kind] || []).map((key) => TABS[key]);
    },
    tab() {
      const tab = this.$route.params.tab;
      return this.tabs.some((el) => el.key === tab) ? tab : "general";
    },
    sub() {
      return this.$route.params.sub || "";
    },
    postbackFormats() {
      return postbackFormatList(this.kind);
    },
    formDirty() {
      return Boolean(this.form) && formSnapshot(this.form) !== this.savedSnapshot;
    },
    dirtySections() {
      return Object.values(this.sections).filter((vm) => vm.sectionDirty);
    },
    dirty() {
      return this.formDirty || this.dirtySections.length > 0;
    },
    // The form fields with errors, by the tab they are on. Only the traffic URLs a choice uses are checked.
    invalidFields() {
      if (!this.form) return [];
      const fields = ["name", "postback_url"].filter((field) => this.$v.form[field].$invalid);
      if (this.kind === KIND_ARBITRAGE) {
        if (this.$v.form.lands.$invalid) fields.push("lands");
        if (this.form.tb_subscription === "user" && this.$v.form.tb_subscription_url.$invalid) fields.push("tb_subscription_url");
        if (this.form.tb_complexity === "user" && this.$v.form.tb_complexity_url.$invalid) fields.push("tb_complexity_url");
      }
      if (this.isSite && allCategoriesDisabled(this.form.disabledCategory)) fields.push("disabledCategory");
      return fields;
    },
    // In the order of the tabs, so a save opens the first one from the left
    invalidTabs() {
      const order = this.tabs.map((tab) => tab.key);
      return [...new Set(this.invalidFields.map((field) => FIELD_TABS[field] || "general"))].sort((a, b) => order.indexOf(a) - order.indexOf(b));
    },
    // Changed or failed tabs and formats, marked by a dot
    tabMarks() {
      const marks = {};
      this.changedTabs.forEach((tab) => (marks[tab] = "warn"));
      if (this.dirtySections.length) marks.formats = "warn";
      Object.keys(this.failed).forEach((key) => {
        const tab = key.startsWith("formats:") ? "formats" : key;
        marks[tab] = "danger";
      });
      if (this.$v.form.$dirty) this.invalidTabs.forEach((tab) => (marks[tab] = "danger"));
      return marks;
    },
    formatMarks() {
      const marks = {};
      this.dirtySections.forEach((vm) => (marks[vm.sectionKey] = "warn"));
      Object.keys(this.failed)
        .filter((key) => key.startsWith("formats:"))
        .forEach((key) => (marks[key.slice("formats:".length)] = "danger"));
      return marks;
    },
    // Tabs whose form fields differ from the saved ones
    changedTabs() {
      if (!this.form || !this.savedSnapshot) return [];
      const saved = JSON.parse(this.savedSnapshot);
      const current = JSON.parse(formSnapshot(this.form));
      const tabs = new Set();
      Object.keys(current).forEach((field) => {
        if (JSON.stringify(current[field]) !== JSON.stringify(saved[field])) tabs.add(this.tabOfField(field));
      });
      return [...tabs];
    },
    // "Unsaved changes in 3 sections": the changed tabs of the form and the changed formats
    saveMessage() {
      const count = this.changedTabs.length + this.dirtySections.length;
      return count > 1 ? this.$t("platform.card.save.unsaved_sections", { count }) : "";
    },
  },
  watch: {
    platformId: {
      immediate: true,
      handler() {
        this.init();
      },
    },
    tab: {
      immediate: true,
      handler(tab) {
        this.$set(this.visited, tab, true);
      },
    },
  },
  beforeRouteLeave(to, from, next) {
    confirmLeave(this, next);
  },
  methods: {
    async init() {
      this.loading = true;
      this.sections = {};
      this.visited = { [this.tab]: true };
      this.failed = {};
      const res = await ServiceSubAccs.getSubAcc(this.platformId);
      if (!(res && res.status === 200 && res.data)) {
        this.subaccount = null;
        this.loading = false;
        return;
      }
      const subaccount = { ...res.data, id: this.platformId };
      this.options = await loadPlatformOptions(platformKind(subaccount));
      this.setSubaccount(subaccount);
      this.resetForm();
      document.title = `Partners.House | ${subaccount.name}`;
      this.loading = false;
      this.$nextTick(() => this.$set(this.visited, this.tab, true));
    },
    setSubaccount(subaccount) {
      this.subaccount = subaccount;
      this.codePlatform = { ...subaccount };
    },
    resetForm() {
      this.form = formFromSubaccount(this.subaccount, this.kind, this.options);
      this.savedSnapshot = formSnapshot(this.form);
      this.$v.$reset();
    },
    tabOfField(field) {
      if (["schedule_days", "mailing_frequency", "push_send_interval", "push_delay_interval"].includes(field)) return "mailing";
      if (["postback_url", "postback_format"].includes(field)) return "postback";
      if (field === "lands") return "landings";
      if (["complexity", "redirect_type", "tb_subscription", "tb_complexity", "tb_subscription_url", "tb_complexity_url", "fullscreen", "enable_in_page_ads", "enable_onclick_ads", "redirect_to_actual_domain", "domain_rotation"].includes(field)) return "traffic";
      return "general";
    },
    openTab(tab, sub) {
      const params = { id: this.platformId, tab };
      if (sub) params.sub = sub;
      this.$router.replace({ name: "PlatformCard", params }).catch(() => {});
    },
    // Checks every changed part; opens the first one with an error
    validate() {
      if (this.formDirty) {
        this.$v.$touch();
        if (this.invalidTabs.length) {
          this.openTab(this.invalidTabs[0]);
          return false;
        }
      }
      const invalidSection = this.dirtySections.find((vm) => !vm.validate());
      if (invalidSection) {
        this.openTab("formats", invalidSection.sectionKey);
        return false;
      }
      return true;
    },
    async saveForm() {
      const res = await ServiceSubAccs.updateSubAcc({ id: this.platformId, params: subaccountPayload(this.subaccount, this.kind, this.form) });
      if (!(res && res.status === 200)) return { ok: false, message: (res && res.message) || this.$t("server_error") };
      // The next payload starts from the saved record again
      const saved = await ServiceSubAccs.getSubAcc(this.platformId);
      if (saved && saved.status === 200 && saved.data) this.setSubaccount({ ...saved.data, id: this.platformId });
      this.savedSnapshot = formSnapshot(this.form);
      this.$v.$reset();
      return { ok: true };
    },
    async save() {
      if (this.saving || !this.dirty) return;
      if (!this.validate()) {
        this.$alert({ title: this.$t("sent_error"), text: this.$t("platform.card.save.invalid") });
        return;
      }
      this.saving = true;
      const jobs = [];
      if (this.formDirty) jobs.push(this.saveForm().then((result) => ({ ...result, key: this.changedTabs[0] || "general", label: this.$t("platform.card.save.main_settings") })));
      this.dirtySections.forEach((vm) => jobs.push(vm.saveSection().then((result) => ({ ...result, key: `formats:${vm.sectionKey}`, label: vm.sectionKey }))));
      const results = await Promise.all(jobs.map((job) => job.catch(() => ({ ok: false, message: this.$t("server_error") }))));
      this.saving = false;

      this.failed = {};
      const failures = results.filter((result) => !result.ok);
      failures.forEach((failure) => failure.key && this.$set(this.failed, failure.key, true));
      if (failures.length) {
        this.$alert({
          title: this.$t("sent_error"),
          text: failures.map((failure) => (failure.label ? `${this.formatName(failure.label)}: ${failure.message}` : failure.message)).join(" "),
          time: 8000,
        });
        return;
      }
      showSettingsSavedAlert(this, this.platformId);
    },
    formatName(key) {
      return this.$te(`platform.card.save.sections.${key}`) ? this.$t(`platform.card.save.sections.${key}`) : key;
    },
    discard() {
      this.resetForm();
      this.failed = {};
      this.dirtySections.forEach((vm) => vm.discardSection());
    },
  },
};
</script>
<style lang="scss" scoped>
.platform-card {
  padding-bottom: 150px;
}

.platform-card__loader {
  display: flex;
  justify-content: center;
  padding: 80px 0;
}

body.dark .platform-card ::v-deep .settings-section {
  border-color: var(--sat-border);
  background: var(--sat-card);
}
</style>
